import { useState, useEffect, useRef, useCallback } from 'react';

// Slim BTC-only market data hook. We reuse the macro dashboard's public
// API Gateway (already cached, monitored, CORS-* allowed) instead of
// duplicating the proxy infrastructure.
//
// What this hook does NOT do, on purpose:
//   - No gold, no FRED, no equity, no on-chain. The dashboard hook does
//     all of those; this product just needs spot + history.
//   - No mock data. Status flips between loading / live / refreshing /
//     stale / error and consumers render accordingly.
const API_BASE = 'https://k15yyed6fc.execute-api.us-east-2.amazonaws.com';
const MARKET_PROXY_URL = `${API_BASE}/market`;
const SPOT_REFRESH_MS = 60_000;

export default function useMarketData({
  spotIntervalMs = SPOT_REFRESH_MS,
} = {}) {
  const [btc, setBtc] = useState({
    spot: null,
    history: [],
    status: 'loading',
    error: null,
    fetchedAt: null,
  });

  const abortRef = useRef(null);
  const historyLoadedRef = useRef(false);

  const refetch = useCallback(async () => {
    abortRef.current?.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;

    setBtc(prev => ({
      ...prev,
      status: prev.spot != null ? 'refreshing' : 'loading',
      error: null,
    }));

    try {
      // Always re-fetch spot (60s cadence). Only fetch history on the
      // first successful run — history is monthly, so refreshing it
      // every minute is pure waste.
      const promises = [fetchBtcSpot(ctl.signal)];
      if (!historyLoadedRef.current) {
        promises.push(fetchBtcHistory(ctl.signal));
      }
      const [spot, historyMaybe] = await Promise.all(promises);

      setBtc(prev => {
        const history = historyMaybe ?? prev.history;
        if (historyMaybe) historyLoadedRef.current = true;
        return {
          spot,
          history,
          status: 'live',
          error: null,
          fetchedAt: new Date(),
        };
      });
    } catch (e) {
      if (e.name === 'AbortError') return;
      setBtc(prev => ({
        ...prev,
        status: prev.spot != null ? 'stale' : 'error',
        error: e.message || String(e),
      }));
    }
  }, []);

  useEffect(() => {
    refetch();
    const id = setInterval(refetch, spotIntervalMs);
    return () => {
      clearInterval(id);
      abortRef.current?.abort();
    };
  }, [refetch, spotIntervalMs]);

  return { btc, refetch };
}

async function fetchBtcSpot(signal) {
  const url = `${MARKET_PROXY_URL}?source=btc-spot`;
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`BTC spot HTTP ${r.status}`);
  const d = await r.json();
  if (d?.error_message) throw new Error(d.error_message);
  if (!Number.isFinite(d?.price)) throw new Error('BTC spot returned no price');
  return d.price;
}

async function fetchBtcHistory(signal) {
  // Yahoo Finance monthly history via the dashboard's market-proxy. We
  // only need the last ~60 days for the 30-day P&L calc, but the proxy
  // returns the full series and chunked transfer is fine.
  const url = `${MARKET_PROXY_URL}?source=btc-history`;
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`BTC history HTTP ${r.status}`);
  const data = await r.json();
  const result = data?.chart?.result?.[0];
  if (!result?.timestamp) throw new Error('BTC history malformed');
  const closes = result.indicators?.quote?.[0]?.close || [];
  return result.timestamp
    .map((t, i) => ({ date: new Date(t * 1000), value: closes[i] }))
    .filter(p => Number.isFinite(p.value));
}
